import "./tinymce.css";
import { useContext, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, IErrorMessage, IUploadedFile, Props } from "./typings";
import SendIcon from "assets/icons/new-outcoming.svg?react";
import AttachmentsIcon from "assets/icons/attachments.svg?react";
import ModalsManagerContext from "contexts/ModalsManager";
import chatService from "api/chatService";
import RichEditor from "components/RichEditor";
import encryptContent from "utils/encryptString";
import { logger } from "utils/logger";
import { readAsBase64 } from "utils/readAsBase64";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import * as openpgp from "openpgp";
import useFiles, { FileExt } from "hooks/useFiles";
import DossierAnimation from "components/DossierAnimation";
import { isAllowedEDeliveryAttachment } from "utils/isAllowedEDeliveryAttachment";
import Filedossier from "components/Filedossier/Filedossier";
import { isAttachmentNameValid } from "utils/isAttachmentNameValid";
import { handleCreate } from "utils/uploadFile";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

const ChatViewNewMessage: FC<Props> = ({
  chatId,
  chatPublicKey,
  onPreview,
  refetch,
}) => {
  const { t } = useTranslation();
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { mutate: sendMessage } = chatService.useSendMessage();


  const [, setIsSending] = useState<boolean>(false);
  const [content, setContent] = useState("");
  const editorRef = useRef<any>(null);
  const wrapperRef = useRef<any>(null);

  const [isDragOver, setIsDragOver] = useState(false);

  const [, setErrorMessage] = useState<IErrorMessage>(null);
  const inputFile = useRef<HTMLInputElement | null>(null);

  const {
    files,
    setFiles,
    addNewFile,
    removeFile,
    size: filesSize,
  } = useFiles();

  const handleUpdateProgressFile = (
    allFiles: FileExt[],
    fileIndex: number,
    progress: number
  ) => {
    const newFiles = [...allFiles];

    const f = newFiles[fileIndex];
    if (f) {
      f.progress = progress;
      setFiles(newFiles);
    }
  };

  const handleChooseFile = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();

    if (true) {
      for (const file of (event.target as any).files) {
        const ext = (file as File).name.split(".").pop();

        if (!isAllowedEDeliveryAttachment(ext)) {
          setErrorMessage({
            title: t("errors.invalidAttachmentFormat"),
            message: t("errors.fileInvalidFormat", { name: (file as File).name }),
            hint: t("errors.supportedFormats"),
          });
          return;
        }
      }
    }

    for (const file of (event.target as any).files) {
      addNewFile(file);
    }

    event.target.value = null;
  };

  const handleClickToUpload = () => {
    if (!inputFile.current) return;
    inputFile.current.click();
  };

  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFile[]>([]);
  const removeUploadedFile = (sindex: number) => {
    setUploadedFiles(uploadedFiles.filter((a, index) => index !== sindex));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const isAncestor = (node, target) => {
    if (node === target) return false;
    while (node.parentNode) {
      if (node.parentNode === target) return true;
      node = node.parentNode;
    }
    return false;
  };

  const handleDragLeave = (e) => {
    if (!wrapperRef.current) return;

    if (!isAncestor(e.relatedTarget, wrapperRef.current)) {
      console.log("Drag leave");
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Required to allow drop
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    console.log("Files dropped:", files);

    for (const file of files) {
      addNewFile(file);
    }
  };

  const handleSend = async () => {
    setIsSending(true);

    logger.log("Encrypting content");

    const decryptedPublicKey = await readFileFromBase64(
      chatPublicKey,
      "application/other"
    );

    const key = await openpgp.key.read(new Uint8Array(decryptedPublicKey));
    // Szyfrowanie treści
    logger.log("Treść przed zaszyfrowaniem", content);
    const encryptedContent = await encryptContent(content, key);

    const normalUri: any = [];
    const normalBlobUri: any = [];
    let encryptedUri: any = [];
    const encryptedBlobUri: any = [];

    if (files.length) {
      logger.log("Creating encrypted files from disk");
      console.time("handleCreate");
      encryptedUri = await handleCreate(
        files,
        "",
        key,
        true,
        true,
        null,
        null,
        (fileIndex: number, progress: number) => {
          handleUpdateProgressFile(files, fileIndex, progress);
        },
        false
      );
      console.timeEnd("handleCreate");
    }

    // if (!!uploadedFiles.length) {
    //   logger.log("Creating unencrypted files from existing files");
    //   console.time("uploadBlob");
    //   encryptedBlobUri = await uploadBlob(
    //     uploadedFiles,
    //     "",
    //     parcelKey,
    //     true,
    //     true
    //   );
    //   console.timeEnd("uploadBlob");
    // }

    const uris = [
      normalUri,
      normalBlobUri,
      encryptedUri,
      encryptedBlobUri,
    ].flat();

    logger.log("Making request to BE");

    sendMessage(
      {
        ChatId: chatId,
        Content: await readAsBase64(encryptedContent),
        Attachments: uris.length
          ? uris?.map((url, index) => ({
              Order: index,
              Uri: url!.uri as string,
            }))
          : [],
      },
      {
        onSuccess: () => {
          refetch?.();
          setContent("");
          setIsSending(false);
          setModals({ ...modals, createPost: false });
          setFiles([]);
        },
        onError(e: any) {
          setIsSending(false);
          notification({
            title: t("errors.messageSendFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  const handleShowPreview = (file: File | IUploadedFile) => {
    if (file instanceof File) {
      const newBlob = new Blob([file], { type: file.type });
      onPreview(newBlob, file.name);
      return;
    }

    onPreview(file.file, file.name);
  };

  const validateAttachment = (file: File) => {
    const ext = file.name.split(".").pop();

    if (!isAllowedEDeliveryAttachment(ext)) {
      return false;
    }

    if (!isAttachmentNameValid(file.name)) {
      return false;
    }

    return true;
  };

  const getAttachmentErrorMessage = (file: File) => {
    const ext = file.name.split(".").pop();

    if (!isAllowedEDeliveryAttachment(ext)) {
      return t("errors.invalidAttachmentFormat");
    }

    if (!isAttachmentNameValid(file.name)) {
      return t("errors.attachmentNameTooLong");
    }

    if (filesSize > 50 * 1024 * 1024)
      return t("errors.messageSizeLimitExceeded");

    return null;
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.newMessage}
        ref={wrapperRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className={styles.content}>
          <div className={styles.editor}>
            <RichEditor
              setContent={setContent}
              ref={editorRef}
              content={content}
              color="#fff"
              autoresize={true}
            />
          </div>
          <div className={styles.attachments}>
            <div className={styles.attachWrap}>
              <div className={styles.attachItems}>
                {files.map((file, i) => (
                  <Filedossier
                    key={`files_${i}`}
                    index={i}
                    actualFile={i}
                    actualProgress={file.progress}
                    file={file}
                    onClick={() => handleShowPreview(file)}
                    handleRemove={removeFile}
                    hasError={!validateAttachment(file)}
                    errorMessage={getAttachmentErrorMessage(file)}
                  />
                ))}
                {uploadedFiles.map((file, i) => (
                  <Filedossier
                    key={`uploadedFiles_${i}`}
                    index={i}
                    file={file}
                    onClick={() => handleShowPreview(file)}
                    handleRemove={removeUploadedFile}
                  />
                ))}
              </div>

              {/* <FileUploader
                  multiple={true}
                  handleChange={async (f: any) => {
                    // handleChooseFile(f);
                    for (const file of f) {
                      addNewFile(file);
                    }
                  }}
                  classes={styles.drop}
                  name="file"
                /> */}
            </div>
          </div>
        </div>

        <div
          className={styles.dragOver}
          style={{ display: isDragOver ? "flex" : "none" }}
        >
          <div className={styles.inner}>
            <div>{t("common.dropFilesHere")}</div>
            <DossierAnimation className={styles.img} />
          </div>
        </div>
        <input
          type="file"
          id="file"
          ref={inputFile}
          multiple={true}
          style={{ display: "none" }}
          onChange={handleChooseFile}
        />

        <div className={styles.buttons}>
          <Tooltip title={t("chat.addAttachment")}>
            <div onClick={handleClickToUpload}>
              <AttachmentsIcon className={styles.icon}></AttachmentsIcon>
            </div>
          </Tooltip>
          <Tooltip title={t("chat.send")}>
            <div onClick={handleSend}>
              <SendIcon className={styles.icon}></SendIcon>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ChatViewNewMessage;
